import * as React from 'react';
import { cva } from 'class-variance-authority';
import { CheckIcon, ChevronDown, XIcon } from 'lucide-react';

import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from 'shadcn/components/ui/Command';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/components/ui/Popover';
import { Separator } from 'shadcn/components/ui/Separator';
import { cn } from 'shadcn/lib/utils';

const multiSelectVariants = cva('m-1 transition ease-in-out delay-150 duration-300', {
    variants: {
        variant: {
            default: 'border-foreground/10 text-foreground bg-card hover:bg-card/80',
            secondary: 'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
            destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
            inverted: 'inverted',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

const MultiSelect = React.forwardRef(
    (
        {
            checkedValues,
            chevronClassName = 'text-muted-foreground',
            className,
            container,
            defaultValue = [],
            maxCount = 3,
            modalPopover = false,
            onClearExtra,
            onToggleOption,
            onValueChange,
            options,
            placeholder = 'Select options',
            placeholderClassName = 'text-muted-foreground',
            value,
            variant,
            ...props
        },
        ref,
    ) => {
        const [uncontrolledSelectedValues, setUncontrolledSelectedValues] = React.useState(defaultValue);
        const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
        const triggerRef = React.useRef(null);
        const isControlled = value !== undefined;
        const selectedValues = isControlled ? value : uncontrolledSelectedValues;
        const effectiveChecked = checkedValues ?? selectedValues;
        const isAllSelected = options.length > 0 && options.every((o) => effectiveChecked.includes(o.value));

        const updateSelectedValues = React.useCallback(
            (nextValues) => {
                if (!isControlled) {
                    setUncontrolledSelectedValues(nextValues);
                }
                onValueChange(nextValues);
            },
            [isControlled, onValueChange],
        );

        // This feels less than ideal, but it's the fastest way to
        // successfully integrate this component.
        React.useImperativeHandle(ref, () => ({
            clearValues: () => {
                updateSelectedValues([]);
            },
            focus: () => triggerRef.current?.focus(),
        }));

        const handleInputKeyDown = (event) => {
            if (event.key === 'Enter') {
                setIsPopoverOpen(true);
            } else if (event.key === 'Backspace' && !event.currentTarget.value) {
                if (onToggleOption && selectedValues.length > 0) {
                    onToggleOption(selectedValues[selectedValues.length - 1]);
                } else {
                    const newSelectedValues = [...selectedValues];
                    newSelectedValues.pop();
                    updateSelectedValues(newSelectedValues);
                }
            }
        };

        const toggleOption = (option) => {
            if (onToggleOption) {
                onToggleOption(option);
                return;
            }
            const newSelectedValues = selectedValues.includes(option)
                ? selectedValues.filter((selectedValue) => selectedValue !== option)
                : [...selectedValues, option];
            updateSelectedValues(newSelectedValues);
        };

        const handleClear = () => {
            updateSelectedValues([]);
        };

        const handleTogglePopover = () => {
            setIsPopoverOpen((prev) => !prev);
        };

        const clearExtraOptions = () => {
            if (onClearExtra) {
                onClearExtra(selectedValues, maxCount);
                return;
            }
            const newSelectedValues = selectedValues.slice(0, maxCount);
            updateSelectedValues(newSelectedValues);
        };

        const toggleAll = () => {
            if (isAllSelected) {
                handleClear();
            } else {
                const allValues = options.map((option) => option.value);
                updateSelectedValues(allValues);
            }
        };

        return (
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
                <PopoverTrigger asChild>
                    <Button
                        ref={triggerRef}
                        {...props}
                        onClick={handleTogglePopover}
                        className={cn(
                            'flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit [&_svg]:pointer-events-auto!',
                            className,
                        )}
                    >
                        {selectedValues.length > 0 ? (
                            <div className="flex justify-between items-center w-full">
                                <div className="flex flex-wrap items-center">
                                    {selectedValues.slice(0, maxCount).map((selectedValue) => {
                                        const option = options.find((o) => o.value === selectedValue);
                                        const IconComponent = option?.icon;
                                        return (
                                            <Badge
                                                key={selectedValue}
                                                className={cn('font-medium', multiSelectVariants({ variant }))}
                                            >
                                                {IconComponent && <IconComponent className="h-4 w-4 mr-2" />}
                                                {option?.badgeLabel || option?.label}
                                                <XIcon
                                                    className="ml-2 h-4 w-4 cursor-pointer"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        toggleOption(selectedValue);
                                                    }}
                                                />
                                            </Badge>
                                        );
                                    })}
                                    {selectedValues.length > maxCount && (
                                        <Badge
                                            className={cn(
                                                'font-medium bg-transparent text-foreground border-foreground/1 hover:bg-transparent',
                                                multiSelectVariants({ variant }),
                                            )}
                                        >
                                            {`+ ${selectedValues.length - maxCount} more`}
                                            <XIcon
                                                className="ml-2 h-4 w-4 cursor-pointer"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    clearExtraOptions();
                                                }}
                                            />
                                        </Badge>
                                    )}
                                </div>
                                <div className="flex items-center justify-between">
                                    <XIcon
                                        className={cn('h-4 mx-2 cursor-pointer', chevronClassName)}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleClear();
                                        }}
                                    />
                                    <Separator orientation="vertical" className="flex min-h-6 h-full" />
                                    <ChevronDown className={cn('h-4 mx-2 cursor-pointer', chevronClassName)} />
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between w-full mx-auto">
                                <span className={cn('font-normal text-sm mx-3', placeholderClassName)}>
                                    {placeholder}
                                </span>
                                <ChevronDown className={cn('h-4 cursor-pointer mx-2', chevronClassName)} />
                            </div>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-auto p-0"
                    align="start"
                    onEscapeKeyDown={() => setIsPopoverOpen(false)}
                    container={container}
                >
                    <Command>
                        <CommandInput placeholder="Search..." onKeyDown={handleInputKeyDown} />
                        <CommandList>
                            <CommandEmpty>No results found.</CommandEmpty>
                            <CommandGroup>
                                <CommandItem key="all" onSelect={toggleAll} className="cursor-pointer">
                                    <div
                                        className={cn(
                                            'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                                            isAllSelected
                                                ? 'bg-primary text-primary-foreground'
                                                : 'opacity-50 [&_svg]:invisible',
                                        )}
                                    >
                                        <CheckIcon className="h-4 w-4" />
                                    </div>
                                    <span>(Select All)</span>
                                </CommandItem>
                                {options.map((option) => {
                                    const isSelected = effectiveChecked.includes(option.value);
                                    return (
                                        <CommandItem
                                            key={option.value}
                                            onSelect={() => toggleOption(option.value)}
                                            className="cursor-pointer"
                                        >
                                            <div
                                                className={cn(
                                                    'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                                                    isSelected
                                                        ? 'bg-primary text-primary-foreground'
                                                        : 'opacity-50 [&_svg]:invisible',
                                                )}
                                            >
                                                <CheckIcon className="h-4 w-4" />
                                            </div>
                                            {option.icon && (
                                                <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                                            )}
                                            <span>{option.label}</span>
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        );
    },
);

export { MultiSelect };
